import React, { useState } from "react";
import { Star } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { logger } from "../../lib/logger";

export const Stars = ({ value, size = 16 }) => (
  <span className="inline-flex items-center gap-0.5" aria-hidden>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} strokeWidth={1.4} className={n <= Math.round(value) ? "text-[#C49A6C] fill-[#C49A6C]" : "text-[#3A362F]"} />
    ))}
  </span>
);

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-AE" : "en-GB", { year: "numeric", month: "short", day: "numeric" }); }
  catch (e) { logger.warn("bad review date", e); return ""; }
};

export const ReviewsSummary = ({ data, t }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
    <div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-[#C49A6C] mb-3">{t("reviews_title")}</p>
      <div className="flex items-center gap-4">
        <span className="serif text-5xl text-[#F2EBE1]" data-testid="reviews-average">{data.count ? data.average.toFixed(1) : "—"}</span>
        <div>
          <Stars value={data.average} size={18} />
          <p className="text-xs text-[#8A857D] mt-1.5" data-testid="reviews-count">
            {data.count > 0
              ? `${t("based_on")} ${data.count} ${t(data.count === 1 ? "reviews_count_label_one" : "reviews_count_label")}`
              : t("reviews_none")}
          </p>
        </div>
      </div>
    </div>
  </div>
);

export const ReviewList = ({ reviews, t, lang }) => (
  <div className="space-y-8" data-testid="reviews-list">
    {reviews.length === 0 && <p className="text-sm text-[#6E685F] italic">{t("reviews_none")}</p>}
    {reviews.map((r, i) => (
      <div key={`${r.created_at}-${r.name}`} className="border-b border-[#1E1C19] pb-8" data-testid={`review-item-${i}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="serif text-lg text-[#F2EBE1]">{r.name || "Anonymous"}</span>
          <span className="text-[11px] uppercase tracking-widest text-[#6E685F]">{fmtDate(r.created_at, lang)}</span>
        </div>
        <Stars value={r.rating} size={14} />
        <p className="text-[15px] text-[#8A857D] leading-[1.8] mt-3">{r.text}</p>
      </div>
    ))}
  </div>
);

const StarPicker = ({ rating, setRating }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="review-rating-input">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
          data-testid={`review-star-${n}`} className="p-1 transition-transform hover:scale-110">
          <Star size={26} strokeWidth={1.3} className={n <= (hover || rating) ? "text-[#C49A6C] fill-[#C49A6C]" : "text-[#3A362F]"} />
        </button>
      ))}
    </div>
  );
};

export const ReviewForm = ({ slug, t, onSubmitted }) => {
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating || !text.trim()) { toast.error(t("review_error")); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${slug}/reviews`, { name: name.trim() || undefined, rating, text: text.trim() });
      toast.success(t("review_thanks"));
      setName(""); setRating(0); setText("");
      onSubmitted();
    } catch (err) {
      logger.error("review submit failed", err);
      toast.error(t("review_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-[#262421] bg-[#141311]/60 backdrop-blur-sm p-8">
      <p className="serif text-2xl text-[#F2EBE1] mb-6">{t("write_review")}</p>
      <form onSubmit={submit} className="space-y-6">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#6E685F] mb-3">{t("your_rating")}</label>
          <StarPicker rating={rating} setRating={setRating} />
        </div>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("review_name_ph")} data-testid="review-name-input"
          className="w-full bg-transparent border-b border-[#262421] py-3 text-sm text-[#F2EBE1] placeholder:text-[#6E685F] focus:border-[#C49A6C] outline-none transition-colors" />
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#6E685F] mb-3">{t("your_review")}</label>
          <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("review_text_ph")} rows={4} data-testid="review-text-input"
            className="w-full bg-transparent border border-[#262421] p-4 text-sm text-[#F2EBE1] placeholder:text-[#6E685F] focus:border-[#C49A6C] outline-none transition-colors resize-none leading-relaxed" />
        </div>
        <button type="submit" disabled={submitting} data-testid="review-submit-btn"
          className="w-full bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#E7CBA9] transition-colors disabled:opacity-50">
          {t("submit_review")}
        </button>
      </form>
    </div>
  );
};
